import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  MapPin,
  Ruler,
  Package,
  CheckCircle2,
  AlertCircle,
  Store,
  ArrowLeft,
  Tag,
  MessageCircle,
  Info,
  ZoomIn,
  Wrench,
  Truck,
  ChevronDown,
} from "lucide-react";
import { api, gbp, floorLabel, formatApiError, BACKEND_URL, STOCK_TYPES, priceBasisLabel } from "@/lib/api";
import { SavingLine } from "@/components/SavingLine";
import { useUnit } from "@/context/UnitContext";
import { mToUnit, unitToM, unitLabel, dimPairLabel } from "@/lib/units";
import PublicHeader from "@/components/PublicHeader";
import Seo, { breadcrumbSchema } from "@/seo/Seo";
import ImageLightbox from "@/components/ImageLightbox";
import { resolveListingImages } from "@/lib/categoryImages";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { unit } = useUnit();
  const [roomW, setRoomW] = useState(() => mToUnit(params.get("room_width"), unit) || "");
  const [roomL, setRoomL] = useState(() => mToUnit(params.get("room_length"), unit) || "");
  const [showReserve, setShowReserve] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Pass the shopper's postcode (and room) so the API can return a genuine
    // distance for THIS listing. Never fabricate a distance when absent.
    const qs = new URLSearchParams();
    const pc = params.get("postcode");
    const rw = params.get("room_width");
    const rl = params.get("room_length");
    if (pc) qs.set("postcode", pc);
    if (rw) qs.set("room_width", rw);
    if (rl) qs.set("room_length", rl);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    api
      .get(`/listings/${id}${suffix}`)
      .then((r) => setListing(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const w = unitToM(roomW, unit);
  const l = unitToM(roomL, unit);
  const roomArea = w && l ? w * l : null;
  const wastagePct = 10; // Public default — admin-configurable server side.
  const requiredCoverage = roomArea ? +(roomArea * (1 + wastagePct / 100)).toFixed(2) : null;

  const isCoverage = listing?.matching_mode === "coverage";
  const packsRequired =
    isCoverage && requiredCoverage && listing.coverage_per_pack_m2
      ? Math.ceil(requiredCoverage / listing.coverage_per_pack_m2)
      : null;

  const fits =
    listing && w && l
      ? isCoverage
        ? packsRequired != null && listing.packs_available != null && packsRequired <= listing.packs_available
        : (listing.width_m >= w && listing.length_m >= l) ||
          (listing.width_m >= l && listing.length_m >= w)
      : null;
  const waste =
    !isCoverage && roomArea && listing ? Math.max(0, (listing.area_m2 || 0) - roomArea) : null;

  const stockPhrase = STOCK_TYPES.find((s) => s.value === listing?.stock_type)?.phrase;
  return (
    <>
      {listing && (
        <Seo
          title={`${listing.title} · ${listing.retailer?.locality || "Greater Manchester"}`}
          description={
            (listing.description || "").slice(0, 150) ||
            `${listing.title} — ${listing.retailer?.label || "Verified Greater Manchester flooring retailer"}. Reserve and collect locally.`
          }
          path={`/listing/${listing.id}`}
          image={resolveListingImages(listing).photos[0]}
          ogType="product"
          structured_data={[
            {
              "@context": "https://schema.org",
              "@type": "Product",
              name: listing.title,
              // Anti-circumvention: brand is intentionally the platform,
              // not the retailer. Individual retailer identity stays hidden
              // until the buyer has paid.
              brand: { "@type": "Brand", name: "Floor-Match" },
              image: resolveListingImages(listing).photos.slice(0, 5),
              description:
                listing.description ||
                `${floorLabel(listing.flooring_type)} · ${listing.condition || "as-new"} · ready for local collection.`,
              offers: {
                "@type": "Offer",
                priceCurrency: "GBP",
                price: listing.price_gbp,
                availability:
                  listing.status === "live"
                    ? "https://schema.org/InStock"
                    : listing.status === "reserved"
                    ? "https://schema.org/OutOfStock"
                    : "https://schema.org/Discontinued",
                areaServed: listing.retailer?.locality || "Greater Manchester",
              },
            },
            breadcrumbSchema([
              { name: "Home", url: `${window.location.origin}/` },
              { name: "Search", url: `${window.location.origin}/search` },
              { name: listing.title, url: `${window.location.origin}/listing/${listing.id}` },
            ]),
          ]}
        />
      )}
      <PublicHeader />
      <main className="min-h-screen" data-testid="listing-detail-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <button
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#E05915] transition-colors mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to results
          </button>

          {loading && <div className="text-slate-500">Loading listing…</div>}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
              {error}
            </div>
          )}

          {listing && (
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7">
                <ListingGallery listing={listing} />
                <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
                  <h2 className="font-display font-semibold text-xl text-slate-900">Description</h2>
                  <p className="text-slate-600 mt-2 leading-relaxed">{listing.description}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 text-sm">
                    <Stat label="Reference" value={listing.reference_code} />
                    <Stat label="Type" value={floorLabel(listing.flooring_type)} />
                    <Stat label="Colour" value={listing.colour} />
                    <Stat label="Condition" value={listing.condition.replace("_", " ")} />
                  </div>
                </div>

                <details
                  className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 group"
                  data-testid="why-lower-price"
                >
                  <summary className="flex items-center justify-between gap-3 cursor-pointer list-none font-display font-semibold text-lg text-slate-900">
                    <span>Why the lower price?</span>
                    <ChevronDown
                      className="w-5 h-5 text-slate-400 shrink-0 transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="mt-3 space-y-2.5 text-sm text-slate-600 leading-relaxed">
                    <p>
                      {stockPhrase
                        ? `This listing is ${stockPhrase} from a retailer's stock. Finding a buyer for a particular size or quantity can take time, so the retailer offers a lower price to help clear it.`
                        : "This listing is part of a retailer's remnant, roll-end or surplus stock. Finding a buyer for a particular size or quantity can take time, so the retailer offers a lower price to help clear it."}
                    </p>
                    <p>
                      The discount does not automatically mean lower quality. Check this item's
                      photographs, condition, dimensions and available quantity before reserving.
                    </p>
                  </div>
                </details>

                <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="retailer-card">
                  <div className="flex items-start gap-4">
                    <span className="grid place-items-center w-12 h-12 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B]">
                      <Store className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <div className="flex-1">
                      <div className="font-display font-semibold text-slate-900">
                        {listing.retailer.label || "Verified Greater Manchester flooring retailer"}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                        {listing.retailer.locality}
                        {listing.distance_label && (
                          <span className="ml-1">· {listing.distance_label}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {listing.retailer.collection_available && (
                          <span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-1">Collection available</span>
                        )}
                        {listing.retailer.fitting_available && (
                          <span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-1">Fitting available</span>
                        )}
                        {listing.retailer.is_verified && (
                          <span className="text-xs bg-[#2A5A3B]/10 text-[#2A5A3B] rounded-full px-2 py-1 font-semibold">Verified</span>
                        )}
                      </div>
                      <div className="text-sm text-slate-700 mt-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <strong className="text-slate-900">Retailer identity revealed after your reservation is confirmed.</strong>{" "}
                        Once your reservation is confirmed, we'll provide the collection details.
                        All messages stay on the platform to protect both sides.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div
                  className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6"
                  data-testid="reserve-panel"
                >
                  <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 capitalize">
                    {listing.colour} {floorLabel(listing.flooring_type)}
                  </h1>
                  <div className="mt-2 text-sm text-slate-500">Ref {listing.reference_code}</div>
                  <div className="mt-4">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display font-bold text-3xl text-slate-900">
                        {gbp(listing.price_gbp)}
                      </span>
                      <span className="text-sm text-slate-500" data-testid="detail-price-basis">{priceBasisLabel(listing)}</span>
                    </div>
                    <SavingLine price={listing.price_gbp} retail={listing.rrp_gbp} testId="detail-saving" className="mt-1 text-sm" />
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    {listing.matching_mode === "coverage" ? (
                      <>
                        <Stat
                          icon={<Package className="w-3.5 h-3.5" />}
                          label="Per pack"
                          value={`${listing.coverage_per_pack_m2}m²`}
                        />
                        <Stat
                          icon={<Package className="w-3.5 h-3.5" />}
                          label="Available"
                          value={`${listing.packs_available} × ${listing.coverage_per_pack_m2}m² = ${listing.total_coverage_m2}m²`}
                        />
                      </>
                    ) : (
                      <>
                        <Stat
                          icon={<Ruler className="w-3.5 h-3.5" />}
                          label="Piece size"
                          value={dimPairLabel(listing.width_m, listing.length_m, unit)}
                        />
                        <Stat
                          icon={<Package className="w-3.5 h-3.5" />}
                          label="Usable area"
                          value={`${listing.area_m2}m²`}
                        />
                      </>
                    )}
                  </div>

                  <div className="mt-5">
                    <div className="text-sm font-semibold text-slate-700 mb-2">
                      Will it fit your room?
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="number"
                        step="any"
                        placeholder={`Width (${unitLabel(unit)})`}
                        value={roomW}
                        onChange={(e) => setRoomW(e.target.value)}
                        className="h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                        data-testid="fit-room-width"
                      />
                      <input
                        type="number"
                        step="any"
                        placeholder={`Length (${unitLabel(unit)})`}
                        value={roomL}
                        onChange={(e) => setRoomL(e.target.value)}
                        className="h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                        data-testid="fit-room-length"
                      />
                    </div>
                    {fits === true && (
                      <div
                        className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 flex items-start gap-2"
                        data-testid="fit-confirm"
                      >
                        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                        <div>
                          {isCoverage ? (
                            <>
                              <div className="font-semibold" data-testid="coverage-enough">
                                ✓ Enough for your room.
                              </div>
                              <div className="text-xs mt-1 space-y-0.5">
                                <div>
                                  Your room: <strong>{dimPairLabel(w, l, unit)} = {roomArea.toFixed(2)}m²</strong>
                                </div>
                                <div>
                                  Recommended coverage: <strong>{requiredCoverage}m²</strong>{" "}
                                  <span className="opacity-75">(includes {wastagePct}% allowance)</span>
                                </div>
                                <div>
                                  Available:{" "}
                                  <strong>
                                    {listing.packs_available} × {listing.coverage_per_pack_m2}m² = {listing.total_coverage_m2}m²
                                  </strong>
                                </div>
                                <div className="font-semibold">
                                  You'll need approximately {packsRequired} pack
                                  {packsRequired === 1 ? "" : "s"}.
                                </div>
                                {listing.price_basis === "per_pack" && (
                                  <div data-testid="packs-total">
                                    {packsRequired} × {gbp(listing.price_gbp)} = <strong>{gbp(listing.price_gbp * packsRequired)}</strong>
                                    <SavingLine price={listing.price_gbp} retail={listing.rrp_gbp} qty={packsRequired} testId="packs-saving" />
                                  </div>
                                )}
                                <div className="text-[11px] opacity-75 pt-1">
                                  FloorMatch includes a standard allowance for cuts and waste.
                                  Complex room shapes or laying patterns may require more flooring.
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="font-semibold">
                                Yes — this fits{(listing.width_m >= w && listing.length_m >= l) ? "" : " when rotated"}.
                              </div>
                              <div className="text-xs mt-0.5">
                                Your room: <strong>{dimPairLabel(w, l, unit)}</strong> · This remnant: <strong>{dimPairLabel(listing.width_m, listing.length_m, unit)}</strong>
                              </div>
                              {waste != null && (
                                <div>
                                  You'll have about <strong>{waste.toFixed(2)}m²</strong> spare — a
                                  handy leftover for cupboards, stairs or matching offcuts.
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {fits === false && (
                      <div
                        className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900 flex items-start gap-2"
                        data-testid="fit-fail"
                      >
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                        <div>
                          {isCoverage
                            ? `Not enough for that room — ${listing.packs_available} pack${listing.packs_available === 1 ? "" : "s"} covers ${listing.total_coverage_m2}m² (you'd need ${packsRequired}). Try a larger listing.`
                            : "This remnant is too small for that room. Try a different one from search."}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    disabled={listing.status !== "live" || fits === false}
                    onClick={() => setShowReserve(true)}
                    className="mt-5 w-full fm-btn-primary disabled:bg-slate-300 disabled:cursor-not-allowed"
                    data-testid="open-reserve-btn"
                  >
                    {listing.status === "live"
                      ? isCoverage
                        ? "Reserve this stock"
                        : "Reserve this remnant"
                      : listing.status === "reserved"
                      ? "Currently reserved"
                      : "Sold — no longer available"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowEnquiry(true)}
                    className="mt-2 w-full h-11 rounded-full border border-slate-300 bg-white hover:bg-slate-50 transition-colors font-semibold text-slate-700 inline-flex items-center justify-center gap-2"
                    data-testid="open-enquiry-btn"
                  >
                    <MessageCircle className="w-4 h-4" aria-hidden="true" /> Ask a question
                  </button>

                  {listing.status !== "live" && (
                    <div
                      className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs"
                      data-testid="unavailable-banner"
                    >
                      {listing.status === "reserved"
                        ? "This remnant is currently secured by another customer. It may return to the marketplace if their collection window expires."
                        : "This remnant has already been sold. Try our search for similar sizes and colours nearby."}
                    </div>
                  )}

                  <div className="mt-4 border-t border-slate-100 pt-4" data-testid="service-options">
                    <div className="text-sm font-semibold text-slate-700 mb-2">From shop to home</div>
                    <ul className="space-y-1.5 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <Store className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" aria-hidden="true" />
                        Collect yourself from the retailer.
                      </li>
                      {listing.delivery_available && (
                        <li className="flex items-start gap-2" data-testid="service-delivery">
                          <Truck className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" aria-hidden="true" />
                          Collection &amp; delivery available
                          {listing.delivery_fee_gbp != null ? ` — ${gbp(listing.delivery_fee_gbp)}, paid directly to the driver.` : ", paid directly to the driver."}
                        </li>
                      )}
                      {listing.retailer.fitting_available && (
                        <li className="flex items-start gap-2" data-testid="service-fitting">
                          <Wrench className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" aria-hidden="true" />
                          Fitting available — tick "Book a fitter" when you reserve.
                        </li>
                      )}
                    </ul>
                    <p className="text-xs text-slate-400 mt-2">
                      Delivery and fitting cost extra and are subject to availability.
                    </p>
                  </div>

                  <div className="mt-3 text-xs text-slate-500 flex items-start gap-1.5">
                    <Tag className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                    Secure your flooring through FloorMatch. Once your reservation is confirmed,
                    we'll provide the collection details.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {showReserve && listing && (
          <ReserveModal listing={listing} roomW={w} roomL={l} defaultPostcode={params.get("postcode") || ""} onClose={() => setShowReserve(false)} />
        )}
        {showEnquiry && listing && (
          <EnquiryModal listing={listing} onClose={() => setShowEnquiry(false)} />
        )}
      </main>
    </>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function ListingGallery({ listing }) {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const { photos, isFallback, alt } = resolveListingImages(listing);
  if (!photos || photos.length === 0) {
    return <div className="rounded-2xl bg-slate-100 aspect-[4/3] border border-slate-200" />;
  }
  return (
    <div data-testid="listing-gallery">
      <button
        type="button"
        onClick={() => setZoom(true)}
        className="group/zoom block w-full rounded-2xl overflow-hidden border border-slate-200 bg-white relative cursor-zoom-in"
        aria-label="Zoom photo"
        data-testid="open-lightbox"
      >
        <img
          src={photos[active]}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full aspect-[4/3] object-cover"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-slate-900/60 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1.5 rounded-full opacity-90 group-hover/zoom:opacity-100 transition-opacity">
          <ZoomIn className="w-3.5 h-3.5" aria-hidden="true" /> Tap to zoom
        </span>
        {isFallback && (
          <span
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-slate-900/70 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full"
            data-testid="illustrative-image-badge"
          >
            <Info className="w-3.5 h-3.5" /> Illustrative image — exact remnant may vary
          </span>
        )}
      </button>
      {photos.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2">
          {photos.map((p, i) => (
            <button
              key={p + i}
              type="button"
              onClick={() => setActive(i)}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                i === active ? "border-[#E05915]" : "border-transparent hover:border-slate-300"
              }`}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Photo ${i + 1}`}
            >
              <img src={p} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {zoom && (
        <ImageLightbox photos={photos} index={active} alt={alt} onClose={() => setZoom(false)} />
      )}
    </div>
  );
}

function ReserveModal({ listing, roomW, roomL, defaultPostcode = "", onClose }) {
  const nav = useNavigate();
  const { unit } = useUnit();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fitting, setFitting] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [jobPostcode, setJobPostcode] = useState(defaultPostcode);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Seed the fit-check dimensions from the on-page checker (if used) so the
  // modal is self-contained — the customer can enter/confirm their room size
  // right here instead of hitting a confusing error that references fields
  // they can't see.
  const [rw, setRw] = useState(() => mToUnit(roomW, unit) || "");
  const [rl, setRl] = useState(() => mToUnit(roomL, unit) || "");

  async function handlePhotos(fileList) {
    const files = Array.from(fileList || []).slice(0, 3 - photos.length);
    if (!files.length) return;
    setUploading(true);
    setPhotoError(null);
    try {
      const next = [...photos];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/reservations/room-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(`${BACKEND_URL}${r.data.url}`);
      }
      setPhotos(next.slice(0, 3));
    } catch (e) {
      setPhotoError(formatApiError(e) || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!rw || !rl) {
      setError("Add your room width and length below so we can confirm this remnant fits.");
      return;
    }
    if ((fitting || delivery) && !jobPostcode.trim()) {
      setError("Add your postcode so we can arrange fitting/delivery near you.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post("/reservations", {
        listing_id: listing.id,
        customer_name: name,
        customer_email: email,
        customer_phone: phone,
        room_width_m: unitToM(rw, unit),
        room_length_m: unitToM(rl, unit),
        fitting_interest: fitting,
        delivery_requested: delivery,
        job_postcode: (fitting || delivery) ? jobPostcode.trim() : null,
        room_photo_urls: fitting ? photos : [],
        notes,
        origin_url: window.location.origin,
      });
      // Stash the reservation id + access token locally so the return-from-
      // Stripe /reservation/success page can render the retailer address
      // once the webhook confirms payment. sessionStorage clears when the
      // browser tab closes; the token is also embedded in the confirmation
      // email so it survives a return on a fresh device.
      try {
        window.sessionStorage.setItem(
          `fm.res.${r.data.stripe_session_id}`,
          JSON.stringify({ reservation_id: r.data.id, access_token: r.data.access_token }),
        );
      } catch (err) {
        // Non-blocking: sessionStorage can be unavailable (private mode / quota).
        // The confirmation email link is the durable fallback, so we only log.
        console.warn("FloorMatch: couldn't stash reservation token; email link remains the fallback.", err);
      }
      // Redirect to hosted Stripe Checkout. On completion Stripe brings the
      // customer back to /reservation/success?session_id=... where we poll
      // /api/payments/status/{sid} and then hop to /reservation/:id.
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      // Fallback (only if Stripe was unavailable and the backend somehow
      // still returned the reservation without a checkout URL).
      nav(`/reservation/${r.data.id}?t=${encodeURIComponent(r.data.access_token || "")}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setError(
          "This remnant has just been reserved by someone else. Browse similar remnants nearby — new roll ends are added daily.",
        );
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="reserve-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 max-h-[92vh] overflow-y-auto">
        <div className="p-5 sm:p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-xl text-slate-900">
              Reserve this listing
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Pay securely online now to reserve it, then collect from your local shop. The shop's name &amp; address unlock the moment payment is confirmed (48&nbsp;h collection window).
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Close"
            data-testid="close-reserve-modal"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-5 sm:p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">Your name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
              data-testid="reserve-name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                data-testid="reserve-email"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Phone</label>
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                data-testid="reserve-phone"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">
              Your room size <span className="font-normal text-slate-400">— so we can confirm it fits</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                step="any"
                min="0"
                required
                placeholder={`Width (${unitLabel(unit)})`}
                value={rw}
                onChange={(e) => setRw(e.target.value)}
                className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                data-testid="reserve-room-width"
              />
              <input
                type="number"
                step="any"
                min="0"
                required
                placeholder={`Length (${unitLabel(unit)})`}
                value={rl}
                onChange={(e) => setRl(e.target.value)}
                className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                data-testid="reserve-room-length"
              />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="book-fitter-section">
            {listing.delivery_available && (
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={delivery}
                  onChange={(e) => setDelivery(e.target.checked)}
                  className="mt-1 accent-[#E05915]"
                  data-testid="reserve-delivery"
                />
                <span>
                  <span className="font-semibold text-slate-900">Collection &amp; delivery</span>
                  {listing.delivery_fee_gbp != null && (
                    <span className="font-semibold text-[#E05915]"> (+{gbp(listing.delivery_fee_gbp)})</span>
                  )}{" "}
                  — a driver brings it to your home. The delivery fee is paid directly to the driver,
                  not in this payment.
                </span>
              </label>
            )}
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={fitting}
                onChange={(e) => setFitting(e.target.checked)}
                className="mt-1 accent-[#E05915]"
                data-testid="reserve-fitting"
              />
              <span>
                <span className="font-semibold text-slate-900">Book a fitter</span> — offer this job
                to approved local fitters near you (optional). Fitting is arranged and priced
                separately with the fitter.
              </span>
            </label>

            {(fitting || delivery) && (
              <div className="mt-1 space-y-3 border-t border-slate-100 pt-3" data-testid="book-fitter-details">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    Your postcode <span className="font-normal text-slate-400">— so we match fitters/drivers near you</span>
                  </label>
                  <input
                    type="text"
                    value={jobPostcode}
                    onChange={(e) => setJobPostcode(e.target.value)}
                    placeholder="e.g. M4 1HW"
                    className="w-full h-11 rounded-md border border-slate-300 px-3 uppercase focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                    data-testid="reserve-job-postcode"
                  />
                </div>
                {fitting && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    Room photos <span className="font-normal text-slate-400">— help the fitter judge access &amp; subfloor (optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {photos.map((p, i) => (
                      <div key={p + i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                        <img src={p} alt={`Room ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                          className="absolute top-0.5 right-0.5 bg-white/90 text-slate-700 rounded-full p-0.5"
                          aria-label="Remove photo"
                          data-testid={`remove-room-photo-${i}`}
                        >
                          <span className="block w-3 h-3 leading-none text-xs">✕</span>
                        </button>
                      </div>
                    ))}
                    {photos.length < 3 && (
                      <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 bg-white grid place-items-center cursor-pointer hover:bg-slate-50 text-slate-500" data-testid="add-room-photo">
                        <span className="text-[10px] text-center leading-tight">Add<br />photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => handlePhotos(e.target.files)}
                          data-testid="room-photo-input"
                        />
                      </label>
                    )}
                  </div>
                  {uploading && (
                    <div className="text-xs text-slate-500 mt-1.5" data-testid="room-photo-uploading">Uploading photo…</div>
                  )}
                  {photoError && (
                    <div className="text-xs text-red-700 mt-1.5" data-testid="room-photo-error">{photoError}</div>
                  )}
                </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">
              Anything else? (optional)
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
              data-testid="reserve-notes"
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span>Remnant price</span>
              <span>{gbp(listing.price_gbp)}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
              <span>Paid securely online now — no extra booking fee.</span>
            </div>
            <div className="border-t border-slate-200 my-2" />
            <div className="flex items-center justify-between font-semibold text-slate-900">
              <span>Total to reserve today</span>
              <span>{gbp(listing.price_gbp)}</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="fm-btn-primary w-full disabled:opacity-70"
            data-testid="confirm-reserve"
          >
            {submitting ? "Reserving…" : "Reserve & pay securely"}
          </button>
        </form>
      </div>
    </div>
  );
}

const PRESETS = [
  { key: "collect_today", label: "Can I collect today?" },
  { key: "fitting_available", label: "Is fitting available?" },
  { key: "colour_accurate", label: "Is the colour accurate to the photo?" },
];

function EnquiryModal({ listing, onClose }) {
  const [preset, setPreset] = useState("collect_today");
  const [otherText, setOtherText] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/enquiries", {
        listing_id: listing.id,
        customer_name: name,
        customer_email: email,
        preset,
        other_text: preset === "other" ? otherText : null,
      });
      setSent(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="enquiry-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 max-h-[92vh] overflow-y-auto">
        <div className="p-5 sm:p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-xl text-slate-900">Ask a quick question</h3>
            <p className="text-sm text-slate-500 mt-1">
              Sent through FloorMatch. Please don't share phone numbers, emails or links — they'll be
              removed automatically to protect both sides.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Close"
            data-testid="close-enquiry-modal"
          >
            ✕
          </button>
        </div>

        {sent ? (
          <div className="p-6 text-center space-y-3" data-testid="enquiry-sent">
            <div className="grid place-items-center w-12 h-12 rounded-full bg-[#2A5A3B]/10 text-[#2A5A3B] mx-auto">
              <CheckCircle2 className="w-6 h-6" aria-hidden="true" />
            </div>
            <h4 className="font-display font-semibold text-lg">Question sent</h4>
            <p className="text-sm text-slate-600 max-w-sm mx-auto">
              A verified retailer will reply through FloorMatch. In the meantime you can still
              secure this remnant so you don't miss out.
            </p>
            <button onClick={onClose} className="fm-btn-primary" data-testid="enquiry-close-done">
              Back to listing
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 gap-2">
              {PRESETS.map((p) => (
                <label
                  key={p.key}
                  className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                    preset === p.key
                      ? "border-[#E05915] bg-[#E05915]/5"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`enquiry-preset-${p.key}`}
                >
                  <input
                    type="radio"
                    name="preset"
                    value={p.key}
                    checked={preset === p.key}
                    onChange={() => setPreset(p.key)}
                    className="accent-[#E05915]"
                  />
                  <span className="text-sm text-slate-800">{p.label}</span>
                </label>
              ))}
              <label
                className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                  preset === "other" ? "border-[#E05915] bg-[#E05915]/5" : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid="enquiry-preset-other"
              >
                <input
                  type="radio"
                  name="preset"
                  value="other"
                  checked={preset === "other"}
                  onChange={() => setPreset("other")}
                  className="accent-[#E05915]"
                />
                <span className="text-sm text-slate-800">Ask another question</span>
              </label>
              {preset === "other" && (
                <textarea
                  rows={3}
                  maxLength={280}
                  placeholder="Type your question (no phone numbers, emails or links)"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  required
                  className="w-full rounded-md border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                  data-testid="enquiry-other-text"
                />
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Your name</label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                  data-testid="enquiry-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30"
                  data-testid="enquiry-email"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="fm-btn-primary w-full disabled:opacity-70"
              data-testid="send-enquiry"
            >
              {submitting ? "Sending…" : "Send question"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

